<template>
  <a-modal
    :title="modalTitle"
    :visible="visible"
    :confirm-loading="loading"
    @cancel="handleCancel"
    :width="700"
    class="premium-modal"
    :footer="null"
  >
    <a-form :model="form" layout="vertical" ref="formRef" class="custom-form">
      <a-form-item label="Parent Klasifikasi">
        <a-input v-if="form.parentName" :value="form.parentName" disabled />
        <span v-else style="color: #bbb;">Root (tidak ada parent)</span>
      </a-form-item>

      <a-form-item v-if="form.parentId || form.parentTempId" label="Opsi Parent">
        <a-input :value="form.parentOpsi" disabled />
        <small style="color: #888;"
          >Node ini muncul saat opsi "{{ form.parentOpsi }}" dipilih</small
        >
      </a-form-item>

      <a-form-item
        label="Judul Klasifikasi"
        name="namaKlasifikasi"
        :rules="[{ required: true, message: 'Judul klasifikasi wajib diisi' }]"
      >
        <a-input v-model:value="form.namaKlasifikasi" placeholder="Contoh: FA Valvular" />
      </a-form-item>

      <a-form-item label="Tandai Sebagai Hasil Akhir">
        <a-switch v-model:checked="form.ditandaiTerakhir" />
        <small style="color: #888; margin-left: 8px;">Tidak ada cabang selanjutnya</small>
      </a-form-item>

      <a-form-item v-if="!form.ditandaiTerakhir" label="Opsi (opsional)">
        <div class="opsi-list">
          <div v-for="(opsi, index) in form.opsiKlasifikasi" :key="index" class="opsi-item">
            <a-input
              v-model:value="form.opsiKlasifikasi[index]"
              :placeholder="`Opsi ${index + 1}`"
            />
            <a-button danger shape="circle" size="small" @click="removeOpsi(index)">
              <minus-outlined />
            </a-button>
          </div>
          <a-button
            v-if="form.opsiKlasifikasi.length < 6"
            type="dashed"
            block
            @click="addOpsi"
          >
            <plus-outlined /> Tambah Opsi
          </a-button>
        </div>
        <small style="color: #888;">Opsional. Jika diisi, minimal 2 opsi.</small>
      </a-form-item>

      <a-form-item label="Algoritma Terkait">
        <a-select
          v-model:value="form.algoritmaTerkaitId"
          placeholder="Pilih algoritma terkait (opsional)"
          allow-clear
          show-search
          :filter-option="filterOption"
        >
          <a-select-option v-for="alg in filteredAlgoritmaList" :key="alg.id" :value="alg.id">
            {{ alg.nama }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <a-form-item label="Kalkulator Terkait">
        <a-select
          v-model:value="form.kalkulatorTerkaitId"
          placeholder="Pilih kalkulator terkait (opsional)"
          allow-clear
          show-search
          :filter-option="filterKalkulatorOption"
        >
          <a-select-option v-for="k in kalkulatorList" :key="k.id" :value="k.id">
            {{ k.title }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <div class="modal-action-buttons" style="justify-content: flex-end; margin-top: 32px;">
        <button type="button" class="cancel-button" @click="handleCancel">BATAL</button>
        <button type="button" class="confirm-button" :disabled="loading" @click="handleOk">
          <span v-if="loading" class="button-spinner"></span>
          SIMPAN
        </button>
      </div>
    </a-form>
  </a-modal>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { message } from 'ant-design-vue'
import { PlusOutlined, MinusOutlined } from '@ant-design/icons-vue'

export default {
  name: 'NodeModal',
  components: { PlusOutlined, MinusOutlined },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    editingNode: {
      type: Object,
      default: null,
    },
    parentNode: {
      type: Object,
      default: null,
    },
    parentOpsi: {
      type: String,
      default: '',
    },
    algoritmaList: {
      type: Array,
      default: () => [],
    },
    kalkulatorList: {
      type: Array,
      default: () => [],
    },
    currentAlgoritmaId: {
      type: String,
      default: null,
    },
    nodes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:visible', 'submit', 'cancel'],
  setup(props, { emit }) {
    const formRef = ref(null)
    const loading = ref(false)

    const form = reactive({
      parentId: null,
      parentTempId: null,
      parentName: '',
      parentOpsi: '',
      namaKlasifikasi: '',
      opsiKlasifikasi: [],
      ditandaiTerakhir: false,
      algoritmaTerkaitId: null,
      kalkulatorTerkaitId: null,
    })

    const modalTitle = computed(() =>
      props.editingNode ? 'Edit Klasifikasi' : 'Tambah Klasifikasi',
    )

    const filteredAlgoritmaList = computed(() => {
      return props.algoritmaList.filter(a => a.id !== props.currentAlgoritmaId)
    })

    // Reset form when modal opens
    watch(
      () => props.visible,
      newVal => {
        if (newVal) {
          resetForm()
        }
      },
    )

    // Clear opsi when ditandaiTerakhir is toggled to true
    watch(
      () => form.ditandaiTerakhir,
      val => {
        if (val) form.opsiKlasifikasi = []
      },
    )

    const resetForm = () => {
      if (props.editingNode) {
        // Edit mode - populate with existing data
        const parent = props.nodes.find(
          n => n.id === props.editingNode.parentId || n.tempId === props.editingNode.parentTempId,
        )
        form.parentId = props.editingNode.parentId || null
        form.parentTempId = props.editingNode.parentTempId || null
        form.parentName = parent?.namaKlasifikasi || ''
        form.parentOpsi = props.editingNode.parentOpsi || ''
        form.namaKlasifikasi = props.editingNode.namaKlasifikasi
        form.opsiKlasifikasi = [...(props.editingNode.opsiKlasifikasi || [])]
        form.ditandaiTerakhir = props.editingNode.ditandaiTerakhir
        form.algoritmaTerkaitId = props.editingNode.algoritmaTerkaitId
        form.kalkulatorTerkaitId = props.editingNode.kalkulatorTerkaitId || null
      } else {
        // Create mode
        if (props.parentNode) {
          if (props.parentNode.id) {
            form.parentId = props.parentNode.id
            form.parentTempId = null
          } else {
            form.parentId = null
            form.parentTempId = props.parentNode.tempId
          }
          form.parentName = props.parentNode.namaKlasifikasi
        } else {
          form.parentId = null
          form.parentTempId = null
          form.parentName = ''
        }
        form.parentOpsi = props.parentOpsi || ''
        form.namaKlasifikasi = ''
        form.opsiKlasifikasi = []
        form.ditandaiTerakhir = false
        form.algoritmaTerkaitId = null
        form.kalkulatorTerkaitId = null
      }
    }

    const addOpsi = () => {
      if (form.opsiKlasifikasi.length === 0) {
        // First click: add 2 fields at once (min 2 opsi required)
        form.opsiKlasifikasi.push('', '')
      } else if (form.opsiKlasifikasi.length < 6) {
        form.opsiKlasifikasi.push('')
      }
    }

    const removeOpsi = index => {
      form.opsiKlasifikasi.splice(index, 1)
    }

    const filterOption = (input, option) => {
      return option.children[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    const filterKalkulatorOption = (input, option) => {
      return option.children[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    const handleOk = async () => {
      try {
        await formRef.value.validate()
      } catch {
        return
      }

      const validOpsi = form.opsiKlasifikasi.filter(o => o.trim() !== '')
      if (validOpsi.length === 1) {
        message.error('Jika menggunakan opsi, minimal 2 opsi harus diisi')
        return
      }

      const nodeData = {
        parentId: form.parentId,
        parentTempId: form.parentTempId,
        parentOpsi: form.parentOpsi || null,
        namaKlasifikasi: form.namaKlasifikasi.trim(),
        opsiKlasifikasi: form.ditandaiTerakhir ? [] : validOpsi,
        ditandaiTerakhir: form.ditandaiTerakhir,
        algoritmaTerkaitId: form.algoritmaTerkaitId || null,
        kalkulatorTerkaitId: form.kalkulatorTerkaitId || null,
      }

      loading.value = true
      emit('submit', {
        nodeData,
        editingNode: props.editingNode,
        done: () => {
          loading.value = false
          emit('update:visible', false)
        },
        error: () => {
          loading.value = false
        },
      })
    }

    const handleCancel = () => {
      emit('update:visible', false)
      emit('cancel')
    }

    return {
      formRef,
      form,
      loading,
      modalTitle,
      filteredAlgoritmaList,
      addOpsi,
      removeOpsi,
      filterOption,
      filterKalkulatorOption,
      handleOk,
      handleCancel,
    }
  },
}
</script>


/* Global overrides for teleported modal content */
<style lang="scss">
.premium-modal .ant-modal-title {
  font-size: 20px;
  font-weight: 700 !important;
  color: #111827;
}
.premium-modal .ant-modal-header {
  border-bottom: none !important;
  text-align: left;
  padding: 24px 24px 0px 24px !important;
}
</style>

<style lang="scss" scoped>
.custom-form :deep(.ant-form-item) {
  margin-bottom: 24px;
}
.custom-form :deep(label.ant-form-item-required) {
  margin-bottom: 0 !important;
  font-weight: 600;
}
.custom-form :deep(.ant-form-item-label > label) {
  font-weight: 600;
  color: #374151;
}
.custom-form :deep(.ant-form-item-label > label::after),
.custom-form :deep(.ant-form-item-label > label.ant-form-item-required::before),
.custom-form :deep(.ant-form-item-label > label.ant-form-item-required::after) {
  display: none !important;
  content: none !important;
}
.premium-modal :deep(.ant-input), .premium-modal :deep(.ant-select-selector) {
  border-radius: 8px;
  border-color: #d1d5db;
  font-size: 14px;
}
:deep(.ant-input::placeholder), :deep(.ant-select-selection-placeholder) {
  color: #798892 !important;
  opacity: 1 !important;
}
.modal-action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.cancel-button {
  max-width: 230px;
  background: #ffffff;
  border: 2px solid #1a1a1a;
  color: #1a1a1a;
  border-radius: 50px;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Barlow', sans-serif !important;
  cursor: pointer;
  transition: all 0.3s;
  height: 40px;
}
.cancel-button:hover {
  background: #1a1a1a;
  color: white;
}
.confirm-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  min-width: 120px;
  background: #e10f28;
  color: white;
  padding: 6px 16px;
  transition: all 0.3s;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Barlow', sans-serif !important;
}
.confirm-button:hover:not(:disabled) {
  background: white;
  border-color: #e10f28;
  color: #e10f28;
}
.confirm-button:disabled {
  border-color: #98a2b3;
  background: #98a2b3;
  color: white;
  cursor: not-allowed;
  opacity: 0.8;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.button-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.opsi-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.opsi-item {
  display: flex;
  gap: 8px;
}

.opsi-item .a-input {
  flex: 1;
}
</style>
